import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import PromotionalContentCardDesign from "@NA/Components/PromotionalContentCardDesign";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { connect, ResolveThunks, useSelector } from "react-redux";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import { MappedLink } from "@NA/Overrides/Widgets/Header/MainNavigation";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import Zone from "@insite/client-framework/Components/Zone";
import { ContentWrapper } from "@insite/content-library/Widgets/Basic/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { getProp } from "@insite/mobius/utilities";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";
import { StyledButton } from "@insite/mobius/Clickable";
import ADIColors from "@NA/Utils/ADIColors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { handleZoneLinkClick } from "@NA/Utils/Utility";

export interface SolutionSectionStyles {
    solutionAndServiceContainer: GridContainerProps;
    solutionAndServiceCategoryGridItem: GridItemProps;
    solutionAndServicePromoGridItem: GridItemProps;
    leftMegaMenuItem: GridItemProps;
    leftMegaMenuLink: LinkPresentationProps;
    leftMegaMenuLinkVideoCenter: LinkPresentationProps;
    rightMegaMenuItem: GridItemProps;
    rightMegaMenuStaticLink: GridItemProps;
    rightMegaMenuLink: LinkPresentationProps;
    rightMegaMenuChildLink: LinkPresentationProps;
    PromoGridItem: GridItemProps;
    PromoOuterGridItem: GridItemProps;
    PromoImage: LazyImageProps;
    PromoImageDescription: LinkPresentationProps;
    PromoImageDetailInfo: TypographyProps;
    PromoButtonLink: LinkPresentationProps;
    PromoDangerouslyTag?: GridItemProps;
    PromoContainer?: InjectableCss;
}

interface SolutionLinkProps {
    title: string;
    url: string;
    type: string;
    id: string;
}

export const solutionSectionStyles: SolutionSectionStyles = {
    solutionAndServiceContainer: {
        css: css`
            > ${GridOffset} {
                width: 100%;
                margin: auto;
                height: initial;
                min-height: 100%;
                max-height: 100vh;
                overflow: hidden auto;
                &::-webkit-scrollbar {
                    width: 4px;
                }

                &::-webkit-scrollbar-track {
                    background: ${getColor("common.background")};
                }

                &::-webkit-scrollbar-thumb {
                    background-color: ${getColor("text.accent")};
                    border-radius: 7px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    css`
                            padding: 0px 110px;
                        `,
                ])}
            }
        `,
    },
    solutionAndServiceCategoryGridItem: {
        css: css`
            padding: 0px;
            max-width: 19%;
            flex-basis: 19%;
            flex-direction: column;
            /* border-right: 2px solid ${getColor("common.border")}; */
            overflow: hidden auto;
            > li {
                max-width: 100%;
                flex-basis: 100%;
                padding: 0px 0px 10px 15px;
                margin-left: 5px;
                font-size: 12px;
                width: 100%;
                max-height: initial;
                flex-basis: content;
                > a {
                    line-height: 20px;
                    outline: none;
                    font-size: 12px;
                    font-weight: 600;
                }
                &.solution-mega-menu-static-link {
                    font-weight: bold;
                    cursor: pointer;
                    margin: 0px 0px 10px 15px;
                    padding: 0px;
                    width: auto;
                    &.nolink {
                        cursor: default;
                        text-decoration: none;
                    }
                    &:focus {
                        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                                    outline-color: ${ADIColors.white};       
                                    outline-style: ${getProp("theme.focus.style", "solid")};
                                    outline-width: 0px;
                                    `,],
                    "min",);
            }}                  
                    }
                    &:focus-visible {
                        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                                outline-color: #09f;     
                                outline-style: ${getProp("theme.focus.style", "solid")};
                                outline-width: 1px;
                                outline-offset:-1px;
                                `,],
                    "min",);
            }}
                    } 
                }
            }
        `,
    },
    solutionAndServicePromoGridItem: {
        css: css`
            max-width: 43%;
            flex-basis: 43%;
            padding: 0px 0 26px 15px;
            > div[class^="Wrapper"] {
                max-width: 100%;
                flex-basis: 100%;
                > div[class^="ContentWrapper"], .wrapper {
                    flex-basis: 100%;
                    max-width: 100%;
                    flex-direction: row;
                    gap: 16px 20px;
                    padding: 0px;
                    display: flex;
                    flex-wrap: wrap;
                    > div {
                        color: ${getColor("text.main")};
                        max-width: calc(100% / 2 - 10px) !important;
                        width: calc(100% / 2 - 10px) !important;
                        min-width: calc(100% / 2 - 10px) !important;
                        flex: 1;
                        img {
                            max-width: 240px;
                            max-height: 100%;
                            display: block;
                        }
                        > div > a {
                            font-size: 12px;
                            font-weight: 700;
                            line-height: 16.37px;
                            margin-top: 10px;
                            width: 100%;
                            span {
                                color: ${getColor("text.main")};
                                display: block;
                            }
                        }
                        p {
                            padding: 0;
                            font-size: 12px;
                            margin: 0;
                            font-weight: 400;
                            line-height: 16.37px;
                        }
                        > a {
                            display: block;
                            padding: 0;
                            font-size: 12px;
                            font-weight: normal;
                            line-height: normal;
                            text-transform: none;
                            text-decoration: underline;
                            text-underline-offset: 2px;
                            span {
                                color: ${getColor("text.main")};
                                display: block;
                            }
                        }
                    }
                }
            }
        `,
    },
    leftMegaMenuItem: {
        css: css`
            &:last-child {
                margin-bottom: 2rem !important;
            }
        `,
    },
    leftMegaMenuLink: {
        css: css`
            > span {
                color: ${getColor("text.main")} !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        `,
    },
    leftMegaMenuLinkVideoCenter: {
        css: css`
            margin: 0px !important;
            > span {
                color: ${getColor("text.main")} !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        `,
    },
    rightMegaMenuItem: {},
    rightMegaMenuStaticLink: {
        css: css`
            color: ${getColor("text.main")};
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px 15px;
            font-weight: 600;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    rightMegaMenuLink: {
        css: css`
            font-weight: bold;
        `,
    },
    rightMegaMenuChildLink: {},
    PromoGridItem: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            flex-direction: row;
        `,
    },
    PromoOuterGridItem: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            flex-direction: column;
        `,
    },
    PromoImage: {
        css: css`
            width: 200px;
            height: 200px;
        `,
    },
    PromoImageDescription: {
        css: css`
            font-size: 12px;
            font-weight: bold;
            margin-top: 10px;
            width: 100%;
        `,
    },
    PromoImageDetailInfo: {
        css: css`
            color: ${getColor("text.main")};
            padding: 0;
            font-size: 12px;
            margin: 0;
            font-weight: normal;
            line-height: normal;
        `,
    },
    PromoButtonLink: {
        css: css`
            display: inline-block;
            padding: 0;
            font-size: 12px;
            font-weight: normal;
            line-height: normal;
            text-transform: none;
            text-decoration: underline;
        `,
    },
    PromoDangerouslyTag: {
        css: css`
            color: ${getColor("text.main")};
            max-width: 100%;
            flex-basis: 100%;
        `,
    },
    PromoContainer: {
        css: css`
            display: contents;
        `,
    },
};

const mapStateToProps = (state: CustomApplicationState) => {
    const billToId = state.context.session.billToId;
    let acNoSession;
    if (billToId) {
        acNoSession = state.data.billTos.byId[billToId]?.customerNumber
    }
    const categoryState = getCategoriesDataView(state);
    return {
        session: state.context.session,
        acNoSession,
        categoryState: categoryState,
        currentState: state,
        toggleDrawer: state.ADIGlobalData.loginDrawerStore.loginDrawer,
    };
};
const mapDispatchToProps = { toggleLoginDrawer, toggleRewardsDrawer };
interface OwnProps {
    link: MappedLink;
    onHandleClose: Function;
    zoneId: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SolutionAndServicesSectionComponent: React.FC<Props> = ({
    session,
    acNoSession,
    link,
    categoryState,
    currentState,
    onHandleClose,
    toggleLoginDrawer,
    toggleRewardsDrawer,
    toggleDrawer,
    zoneId,
}: Props) => {
    const [activeChildIndex, setActiveChildIndex] = useState<number | null>(null);
    const handleMouseEnter = (index: number, categoryName: string) => {
        setActiveChildIndex(index);
    };
    const [solutionChildItems, setSolutionChildItems] = useState<any[]>([]);
    const [serviceChildItems, setServiceChildItems] = useState<any[]>([]);
    const [addtioanlLearningsChildItems, setAddtioanlLearningsChildItems] = useState<any[]>([]);
    const histroy = useHistory();
    const handleMouseLeave = () => {
        setActiveChildIndex(null);
    };
    const styles = solutionSectionStyles;
    const externalLinks = ["/additional-learnings/video-center"];

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const userloyaltyState = useSelector((state: CustomApplicationState) => state.ADIGlobalData?.loyaltyRewardUserStore);
    const userRoles = useSelector((state: CustomApplicationState) => state.context.session.userRoles)
    const isProRewardsUser = userRoles && userRoles.includes("ADIProRewards");
    const accNoLTY = userloyaltyState.user.userId != undefined ? userloyaltyState.user.userId : "";
    const accNoSession = acNoSession?.toString().split('-')[0];
    const userNameSession = useSelector((state: CustomApplicationState) => state.context.session.userName?.toLocaleLowerCase());
    const emailLTY = userloyaltyState.user.email != undefined ? userloyaltyState.user.email?.toLocaleLowerCase() : "";
    const isRewardsInvitedUser = (accNoLTY == accNoSession && userloyaltyState.user.isEnrolled == false && userNameSession == emailLTY &&
        !isProRewardsUser &&
        (userloyaltyState.user.status?.toLocaleUpperCase() == "A" ||
            userloyaltyState.user.status?.toLocaleUpperCase() == "B" ||
            userloyaltyState.user.status?.toLocaleUpperCase() == "O")
    );
    const hmiRewardsSettingSettings: any = useSelector(
        (state: CustomApplicationState) => state.context.settings.settingsCollection.hmiRewardsSettingSettings);
    const customerSetttings: any = useSelector((state: CustomApplicationState) => state.context.settings.settingsCollection.customerSettings);
    const isXtenRole = userRoles && userRoles.includes(customerSetttings.allowedUserRole);
    const xtenMenuName = customerSetttings.xtenMenuName;
    const zoneRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (toggleDrawer)
            toggleLoginDrawer({ loginDrawer: false });
        toggleRewardsDrawer({ rewardsDrawer: false });
    }, []);


    useEffect(() => {
        const pages: any = currentState.links.pageLinks[0].children;

        const solutionItems = pages.filter((item: { url: string }) => item.url.toLowerCase().trim() === "/solutions");

        if (solutionItems && solutionItems[0].children) {
            setSolutionChildItems(solutionItems[0].children.filter((child: any) => !child.excludeFromNavigation));
        }
        const serviceItems = pages.filter((item: { url: string }) => item.url.toLowerCase().trim() === "/services");

        if (serviceItems && serviceItems[0].children) {
            setServiceChildItems(serviceItems[0].children.filter((child: any) => !child.excludeFromNavigation));
        }
        const addtionalLearningItems = pages.filter(
            (item: { url: string }) => item.url.toLowerCase().trim() === "/additional-learnings",
        );

        if (addtionalLearningItems && addtionalLearningItems.length && addtionalLearningItems[0].children) {
            setAddtioanlLearningsChildItems(
                addtionalLearningItems[0].children.filter((child: any) => !child.excludeFromNavigation),
            );
        }
    }, []);

    const renderPath = (inputString: string) => {
        const parts = inputString.split("/");
        const result = "/" + parts[2];
        if (result == "/training-events") {
            return "/training-events";
        } else {
            return result;
        }
    };

    const checkIfUrlAsExternlLinks = (url: string) => {
        return externalLinks.includes(url);
    };

    const checkRewardsInvitedUser = (child: SolutionLinkProps) => {
        if (isAuthenticated && isRewardsInvitedUser) {
            return hmiRewardsSettingSettings.hmiActivatePageUrl;
        }
        else
            return child.url;
    };

    const TextWrapper = styled.div`
        color: ${getProp("theme.colors.text.main")};
        cursor: pointer;
        font-weight: 600;
        &:hover {
            text-decoration: underline;
        }
    `;
    const naviagteToOtherWindow = (url: string) => {
        window.open(getExternlUrl(url), "_blank");
        onHandleClose();
    };

    function checkIFUrlIsExternal(url: string) {
        return url.toLowerCase().includes("http://") || url.toLowerCase().includes("https://");
    }

    const getExternlUrl = (inputString: string) => {
        if (inputString == "/additional-learnings/video-center") {
            return "https://videos.adiglobal.com/video-center";
        } else {
            return '/';
        }
    };

    return (
        <GridContainer {...styles.solutionAndServiceContainer}>
            {solutionChildItems.length && (
                <GridItem {...styles.solutionAndServiceCategoryGridItem}>
                    <GridItem
                        tabIndex={0}
                        className="solution-mega-menu-static-link"
                        key={`solution-static-link`}
                        {...styles.rightMegaMenuStaticLink}
                        as="li"
                        onClick={() => {
                            histroy.push("/solutions");
                            onHandleClose();
                        }}
                    >
                        {translate("Solutions")}
                    </GridItem>
                    {solutionChildItems?.map((child: SolutionLinkProps, childIndex: number) => (
                        <GridItem
                            className="solution-mega-menu-left-grid-item"
                            {...styles.leftMegaMenuItem}
                            width={(12 / link.numberOfColumns!) as GridWidths}
                            key={childIndex}
                            onMouseOver={() => handleMouseEnter(childIndex, child.title)}
                            onMouseLeave={handleMouseLeave}
                            as="li"
                        >
                            <Link
                                className="solution-mega-menu-left-link"
                                {...styles.leftMegaMenuLink}
                                href={child.url}
                                data-category-name={`selectedCategoryName_${child.title}`}
                                onClick={() => onHandleClose()}
                            >
                                {child.title}
                            </Link>
                        </GridItem>
                    ))}
                </GridItem>
            )}
            {serviceChildItems.length && (
                <GridItem {...styles.solutionAndServiceCategoryGridItem}>
                    <GridItem
                        tabIndex={0}
                        className="solution-mega-menu-static-link"
                        key={`solution-static-link`}
                        {...styles.rightMegaMenuStaticLink}
                        as="li"
                        onClick={() => {
                            histroy.push("/services");
                            onHandleClose();
                        }}
                    >
                        {translate("Services")}
                    </GridItem>
                    {serviceChildItems?.map((child: SolutionLinkProps, childIndex: number) => (
                        <GridItem
                            className="solution-mega-menu-left-grid-item"
                            {...styles.leftMegaMenuItem}
                            width={(12 / link.numberOfColumns!) as GridWidths}
                            key={childIndex}
                            onMouseOver={() => handleMouseEnter(childIndex, child.title)}
                            onMouseLeave={handleMouseLeave}
                            as="li"
                        >
                            {!checkIfUrlAsExternlLinks(child.url) ? (
                                (child.title.toLocaleLowerCase() !== xtenMenuName.toLocaleLowerCase() || (isAuthenticated && isXtenRole && child.title.toLocaleLowerCase() === xtenMenuName.toLocaleLowerCase())) &&
                                <Link
                                    className="solution-mega-menu-left-link"
                                    {...styles.leftMegaMenuLink}
                                    href={(isAuthenticated && child.title.toLocaleLowerCase() === 'adi pro rewards') ?
                                        checkRewardsInvitedUser(child) : child.url}
                                    data-category-name={`selectedCategoryName_${child.title}`}
                                    onClick={() => onHandleClose()}
                                >
                                    {child.title.toLocaleLowerCase() === xtenMenuName.toLocaleLowerCase() ? xtenMenuName : child.title}
                                </Link>
                            ) : (
                                <Link
                                    onClick={() => naviagteToOtherWindow((isAuthenticated &&
                                        child.title.toLocaleLowerCase() === 'adi pro rewards') ?
                                        checkRewardsInvitedUser(child) : child.url)}
                                    className="solution-mega-menu-left-link"
                                    {...styles.leftMegaMenuLinkVideoCenter}
                                        data-category-name={`selectedCategoryName_${child.title}`}
                                    >
                                    {child.title}
                                </Link>
                            )}
                        </GridItem>
                    ))}
                </GridItem>
            )}

            {addtioanlLearningsChildItems.length && (
                <GridItem {...styles.solutionAndServiceCategoryGridItem}>
                    <GridItem
                        className="solution-mega-menu-static-link nolink"
                        key={`solution-static-link`}
                        {...styles.rightMegaMenuStaticLink}
                        as="li"
                        onClick={() => undefined}
                    >
                        {translate("Additional Learnings")}
                    </GridItem>
                    {addtioanlLearningsChildItems?.map((child: SolutionLinkProps, childIndex: number) => (
                        <GridItem
                            className="solution-mega-menu-left-grid-item"
                            {...styles.leftMegaMenuItem}
                            width={(12 / link.numberOfColumns!) as GridWidths}
                            key={childIndex}
                            onMouseOver={() => handleMouseEnter(childIndex, child.title)}
                            onMouseLeave={handleMouseLeave}
                            as="li"
                        >
                            {!checkIfUrlAsExternlLinks(child.url) ? (
                                <Link
                                    className="solution-mega-menu-left-link"
                                    {...styles.leftMegaMenuLink}
                                    href={renderPath(child.url)}
                                    data-category-name={`selectedCategoryName_${child.title}`}
                                    onClick={() => onHandleClose()}
                                >
                                    {child.title}
                                </Link>
                            ) : (
                                <Link
                                    onClick={() => naviagteToOtherWindow(child.url)}
                                    className="solution-mega-menu-left-link"
                                    {...styles.leftMegaMenuLinkVideoCenter}
                                        data-category-name={`selectedCategoryName_${child.title}`}
                                >
                                    {child.title}
                                </Link>
                            )}
                        </GridItem>
                    ))}
                </GridItem>
            )}

            <StyledWrapper {...styles.PromoContainer} ref={zoneRef}
                onClick={(event) => handleZoneLinkClick(event, onHandleClose, zoneRef)}>
                 <GridItem {...styles.solutionAndServicePromoGridItem}>
                    <Zone zoneName="SolutionandService" contentId={zoneId} />
                 </GridItem>
            </StyledWrapper>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SolutionAndServicesSectionComponent);
